import { useCallback, useRef, useEffect, useState } from 'react';
import { SmartCache } from '../utils/smartCache';
/**
 * Hook for using smart cache in React components
 */
export const useSmartCache = (options = {}) => {
    const { cacheKey = 'default', autoCleanup = true, enableDebug = false, ...cacheOptions } = options;
    // Create cache instance with ref to persist across renders
    const cacheRef = useRef(null);
    const [stats, setStats] = useState(null);
    // Initialize cache if not exists
    if (!cacheRef.current) {
        cacheRef.current = new SmartCache(cacheOptions);
        if (enableDebug) {
        }
    }
    const cache = cacheRef.current;
    // Auto-cleanup effect
    useEffect(() => {
        if (!autoCleanup)
            return;
        const cleanup = () => {
            if (cache) {
                cache.stop();
                if (enableDebug) {
                }
            }
        };
        return cleanup;
    }, [cache, autoCleanup, cacheKey, enableDebug]);
    // Stats update effect
    useEffect(() => {
        if (!enableDebug)
            return;
        const updateStats = () => {
            const currentStats = cache.getStats();
            setStats(currentStats);
        };
        const interval = setInterval(updateStats, 5000); // Update stats every 5 seconds
        return () => clearInterval(interval);
    }, [cache, enableDebug]);
    // Wrapped cache methods with debug logging
    const get = useCallback((key) => {
        const result = cache.get(key);
        if (enableDebug) {
        }
        return result;
    }, [cache, enableDebug]);
    const set = useCallback((key, data, ttl, metadata) => {
        cache.set(key, data, ttl, metadata);
        if (enableDebug) {
        }
    }, [cache, enableDebug]);
    const has = useCallback((key) => {
        const result = cache.has(key);
        if (enableDebug) {
        }
        return result;
    }, [cache, enableDebug]);
    const deleteKey = useCallback((key) => {
        const result = cache.delete(key);
        if (enableDebug) {
        }
        return result;
    }, [cache, enableDebug]);
    const clear = useCallback(() => {
        cache.clear();
        if (enableDebug) {
        }
    }, [cache, cacheKey, enableDebug]);
    const invalidateByTag = useCallback((tag) => {
        const count = cache.invalidateByTag(tag);
        if (enableDebug) {
        }
        return count;
    }, [cache, enableDebug]);
    const invalidateByPattern = useCallback((pattern) => {
        const count = cache.invalidateByPattern(pattern);
        if (enableDebug) {
        }
        return count;
    }, [cache, enableDebug]);
    const getStats = useCallback(() => {
        return cache.getStats();
    }, [cache]);
    return {
        get,
        set,
        has,
        delete: deleteKey,
        clear,
        invalidateByTag,
        invalidateByPattern,
        getStats,
        cache
    };
};
/**
 * Hook for caching API responses
 */
export const useApiCache = (options = {}) => {
    const cache = useSmartCache(options);
    const cachedFetch = useCallback(async (url, options, ttl) => {
        const cacheKey = `api:${url}:${JSON.stringify(options || {})}`;
        // Try to get from cache first
        const cached = cache.get(cacheKey);
        if (cached) {
            return cached;
        }
        // Fetch from API
        const response = await fetch(url, options);
        const data = await response.json();
        // Cache the result
        cache.set(cacheKey, data, ttl, {
            tags: ['api', 'fetch'],
            priority: 'medium'
        });
        return data;
    }, [cache]);
    const invalidateApiCache = useCallback((pattern) => {
        if (pattern) {
            return cache.invalidateByPattern(pattern);
        }
        return cache.invalidateByTag('api');
    }, [cache]);
    return {
        ...cache,
        cachedFetch,
        invalidateApiCache
    };
};
/**
 * Hook for caching computed values
 */
export const useComputedCache = (computeFn, dependencies, options = {}) => {
    const cache = useSmartCache(options);
    const [value, setValue] = useState(null);
    const getComputedValue = useCallback(() => {
        const cacheKey = `computed:${JSON.stringify(dependencies)}`;
        // Try to get from cache
        const cached = cache.get(cacheKey);
        if (cached !== null) {
            setValue(cached);
            return cached;
        }
        // Compute new value
        const computed = computeFn();
        // Cache the result
        cache.set(cacheKey, computed, undefined, {
            tags: ['computed'],
            priority: 'high'
        });
        setValue(computed);
        return computed;
    }, [cache, computeFn, dependencies]);
    useEffect(() => {
        getComputedValue();
    }, [getComputedValue]);
    return {
        value,
        refresh: getComputedValue,
        ...cache
    };
};
/**
 * Hook for caching form data
 */
export const useFormCache = (formId, options = {}) => {
    const cache = useSmartCache(options);
    const saveFormData = useCallback((data) => {
        const cacheKey = `form:${formId}`;
        cache.set(cacheKey, data, 30 * 60 * 1000, {
            tags: ['form', formId],
            priority: 'medium'
        });
    }, [cache, formId]);
    const loadFormData = useCallback(() => {
        const cacheKey = `form:${formId}`;
        return cache.get(cacheKey);
    }, [cache, formId]);
    const clearFormData = useCallback(() => {
        const cacheKey = `form:${formId}`;
        cache.delete(cacheKey);
    }, [cache, formId]);
    const clearAllForms = useCallback(() => {
        cache.invalidateByTag('form');
    }, [cache]);
    return {
        saveFormData,
        loadFormData,
        clearFormData,
        clearAllForms,
        ...cache
    };
};
/**
 * Hook for caching user preferences
 */
export const usePreferencesCache = (userId, options = {}) => {
    const cache = useSmartCache({
        ...options,
        defaultTtl: 60 * 60 * 1000, // 1 hour for preferences
        maxSize: 100
    });
    const savePreference = useCallback((key, value) => {
        const cacheKey = `pref:${userId}:${key}`;
        cache.set(cacheKey, value, undefined, {
            tags: ['preferences', userId],
            priority: 'high'
        });
    }, [cache, userId]);
    const getPreference = useCallback((key) => {
        const cacheKey = `pref:${userId}:${key}`;
        return cache.get(cacheKey);
    }, [cache, userId]);
    const clearUserPreferences = useCallback(() => {
        cache.invalidateByTag(userId);
    }, [cache, userId]);
    return {
        savePreference,
        getPreference,
        clearUserPreferences,
        ...cache
    };
};
/**
 * Debug hook for monitoring cache performance
 */
export const useCacheDebug = (cache) => {
    const [stats, setStats] = useState(cache.getStats());
    useEffect(() => {
        const interval = setInterval(() => {
            setStats(cache.getStats());
        }, 1000);
        return () => clearInterval(interval);
    }, [cache]);
    return stats;
};
